import { useEffect, useRef, useState } from "react";
import type {
  Appointment,
  CalendarChair,
  CalendarFilters,
  CombinedLocation,
} from "@dentacore/shared-types";
import { passesFilters } from "@dentacore/shared-types";

import { useAwaitingShown, stateOf } from "./block-state";
import { CardBody, cardColour } from "./booking-card";
import {
  CARD_MIN_PX,
  MIN_HOUR_PX,
  blocksTheChair,
  clockOfMinutes,
  minutesOfClock,
  packLanes,
  placeOnDay,
  wallClock,
} from "./grid-geometry";
import { HourPager } from "./day-grid";
import type { Capacity, Zoom } from "./hour-window";
import { useDayWindow } from "./hour-window";
import { LocationBadge } from "./location-badge";
import { instantOfWallClock } from "./zoned-time";
import styles from "./day-grid.module.css";
import own from "./combined-day-grid.module.css";

/**
 * **Several locations in one day grid** (CF-D1, CF-D13, CF-D17) — one grid,
 * one clock, never split.
 *
 * Its columns are **doctors** (they move between locations) or, on the
 * Chairs switch, **every shown location's chairs in one row**, each headed
 * with its location's badge. Each booking sits at its own location's
 * wall-clock time (CF-D14) and names its location with a badge.
 *
 * ── What it does not do ─────────────────────────────────────────────────────
 *
 * **Dragging moves the time only, inside the booking's own column** (OQ-D13):
 * same location, chair and doctor, the minute read on the booking's own
 * location's clock (CF-D14), sent through the same move the panel's Reschedule
 * makes — so Google sync follows as it does for any move. Released over another
 * location's chairs, the card goes back and says how to move it there: cancel
 * and book again. There is no cross-location reschedule. Clicking empty space
 * in a chair's column books there.
 */

/** What a drop on another location says (OQ-D13). */
export const OTHER_LOCATION_DROP =
  "To move to another location, cancel this booking and book it there.";
/** A drag snaps to this many minutes, as the single-location grid's does. */
const DRAG_STEP = 5;
/** Below this many pixels a press is a click, not a drag. */
const DRAG_SLOP = 4;

/** Below this a column draws its bookings compact: badge, circle, start, first name. */
const COMPACT_BELOW_PX = 110;
/** The day's floor (H3, widened by T5): never less than 08:00–18:00. */
const FLOOR_OPENS = 8 * 60;
const FLOOR_CLOSES = 18 * 60;

export type CombinedColumns = "doctors" | "chairs";

interface Column {
  key: string;
  /** A doctor's id, or null for bookings nobody is named on. */
  providerId?: string | null;
  chair?: CalendarChair & { branchId: string };
  head: React.ReactNode;
  holds: (appointment: Appointment) => boolean;
}

export function CombinedDayGrid({
  date,
  locations,
  columns: by,
  filters,
  providerName,
  onlyColumn,
  selectedId,
  onSelect,
  onColumnName,
  onSlot,
  now,
  hoursShown = null,
  onCapacity,
  onMove,
  onRefused,
}: {
  date: string;
  locations: readonly CombinedLocation[];
  columns: CombinedColumns;
  filters: CalendarFilters;
  providerName: (providerId: string) => string;
  /** The doctor or chair showing alone, so its head reads as pressed. */
  onlyColumn: string | null;
  selectedId: string | null;
  onSelect: (appointment: Appointment) => void;
  /** A column's name is a shortcut to Only for it (CF-D16). */
  onColumnName: (kind: "providerIds" | "chairIds", id: string) => void;
  /** Clicking empty space in a chair's column, where the caller may book. */
  onSlot?: (branchId: string, chairId: string, startMinute: number) => void;
  now?: Date;
  /** How many hours show at once (CZ-D1); null is the whole day. */
  hoursShown?: Zoom;
  /** Told how many hours fit at the minimum hour height, and how long the day is (CZ-D4). */
  onCapacity?: (capacity: Capacity) => void;
  /**
   * A booking dragged up or down in its own column (OQ-D13): the new start, an
   * instant worked out on its own location's clock, its chair unchanged.
   * Absent for someone who may not move bookings, and nothing drags.
   */
  onMove?: (appointment: Appointment, startsAt: Date, chairId: string) => void;
  /** Said when a drop is refused — onto another location's chairs. */
  onRefused?: (message: string) => void;
}): React.JSX.Element {
  /** The card being dragged, and how far, in px. */
  const [drag, setDrag] = useState<{ id: string; fromY: number; dy: number } | null>(null);
  const awaitingShown = useAwaitingShown();
  const box = useRef<HTMLDivElement | null>(null);
  const rows = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  const shown = locations.filter((location) => !filters.branchIds.includes(location.branch.id));
  const locationOf = new Map(locations.map((location) => [location.branch.id, location]));

  /**
   * **The whole day fits the window** (FS-D1): the rows take what is left
   * under the heads, measured, and the minutes are scaled to it.
   */
  useEffect(() => {
    const element = rows.current;
    if (element === null) return;
    const measure = (): void => {
      const top = element.getBoundingClientRect().top;
      const main = element.closest("main");
      const bottom =
        main === null
          ? window.innerHeight - 12
          : main.getBoundingClientRect().bottom - parseFloat(getComputedStyle(main).paddingBottom);
      const height = Math.max(320, Math.floor(bottom - top - 12));
      const width = element.clientWidth;
      setSize((was) => (was.height === height && was.width === width ? was : { width, height }));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    window.addEventListener("resize", measure);
    // **Re-measured when anything above moves it**, as the single-location grid
    // is: a toolbar that wraps once the zoom's label changes pushes the rows
    // down without changing their size, and the page then scrolled by that much.
    let pending = 0;
    const watcher = new MutationObserver(() => {
      if (pending !== 0) return;
      pending = window.setTimeout(() => {
        pending = 0;
        measure();
      }, 30);
    });
    watcher.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["class", "hidden"],
    });
    return () => {
      observer.disconnect();
      watcher.disconnect();
      window.removeEventListener("resize", measure);
      if (pending !== 0) window.clearTimeout(pending);
    };
  }, []);

  // Every booking that is drawn: holds its chair (or is a no-show, drawn struck),
  // on this day at its own location, and passes the filters.
  const placed = shown.flatMap((location) =>
    location.appointments.flatMap((appointment) => {
      if (!blocksTheChair(appointment)) return [];
      if (!passesFilters(appointment, filters)) return [];
      const at = placeOnDay(appointment, location.branch.timezone, date);
      return at === null ? [] : [{ appointment, location, ...at }];
    }),
  );

  // Hours: earliest opening to latest closing of the shown locations, never
  // below the floor, stretched for any booking outside them.
  let opens = FLOOR_OPENS;
  let closes = FLOOR_CLOSES;
  for (const location of shown) {
    const day = location.days[0];
    if (day?.opensAt != null) opens = Math.min(opens, minutesOfClock(day.opensAt));
    if (day?.closesAt != null) closes = Math.max(closes, minutesOfClock(day.closesAt));
  }
  for (const item of placed) {
    opens = Math.min(opens, Math.floor(item.startsAt / 60) * 60);
    closes = Math.max(closes, Math.ceil(item.endsAt / 60) * 60);
  }
  opens = Math.floor(opens / 60) * 60;
  closes = Math.ceil(closes / 60) * 60;

  // The clock's "now", on the first shown location's clock — where one
  // location's day has not begun the line is simply not drawn.
  const nowAt = nowMinutes(shown[0]?.branch.timezone ?? null, date, now);

  // Zoomed in, the hours drawn are a window on that day, paged (CZ-D1).
  // Never shorter than the minimum hour (CZ-D4); what does not fit is paged.
  const capacity: Capacity | null =
    size.height === 0
      ? null
      : {
          fits: Math.max(1, Math.floor(size.height / MIN_HOUR_PX)),
          day: (closes - opens) / 60,
        };
  const {
    window: hourWindow,
    page,
    pageable,
  } = useDayWindow({ startsAt: opens, endsAt: closes }, hoursShown, capacity, date, nowAt);
  const wheel = useRef(0);
  useEffect(() => {
    if (capacity !== null) onCapacity?.(capacity);
  }, [capacity?.fits, capacity?.day]);
  if (hourWindow !== null) {
    opens = hourWindow.startsAt;
    closes = hourWindow.endsAt;
  }
  const pxPerMinute = (size.height || 500) / (closes - opens);
  const y = (minute: number): number => (minute - opens) * pxPerMinute;
  const hours: number[] = [];
  for (let t = opens + 60; t < closes; t += 60) hours.push(t);

  const columns = buildColumns();
  const width = columns.length === 0 ? 0 : (size.width - GUTTER) / columns.length;
  const compactColumns = width > 0 && width < COMPACT_BELOW_PX;

  // The stretches when every shown chair is busy: a red mark on the clock's edge.
  const allChairs = shown.flatMap((location) =>
    location.chairs
      .filter((chair) => !filters.chairIds.includes(chair.id))
      .map((chair) => chair.id),
  );
  const fullRuns: [number, number][] = [];
  if (allChairs.length > 1) {
    for (let t = opens; t < closes; t += 15) {
      const busy = allChairs.filter((chairId) =>
        placed.some(
          (item) =>
            item.appointment.chairId === chairId &&
            item.appointment.status !== "NO_SHOW" &&
            item.startsAt < t + 15 &&
            t < item.endsAt,
        ),
      ).length;
      if (busy < allChairs.length) continue;
      const last = fullRuns[fullRuns.length - 1];
      if (last !== undefined && last[1] === t) last[1] = t + 15;
      else fullRuns.push([t, t + 15]);
    }
  }

  return (
    <div
      className={own.sheet}
      ref={box}
      data-testid="combined-day-grid"
      data-columns={by}
      data-hours-shown={hoursShown ?? "day"}
    >
      <div
        className={compactColumns ? `${own.heads} ${own.narrow}` : own.heads}
        style={{ gridTemplateColumns: template(columns.length) }}
      >
        <div className={own.corner}>
          {pageable && hourWindow !== null && <HourPager window={hourWindow} onPage={page} />}
        </div>
        {columns.map((column) => (
          <div key={column.key} className={own.head} data-testid="combined-column-head">
            {column.head}
          </div>
        ))}
      </div>

      <div
        className={own.rows}
        ref={rows}
        style={{ gridTemplateColumns: template(columns.length), height: size.height || undefined }}
        onWheel={(event) => {
          // A wheel over a zoomed-in day pages it an hour at a time.
          if (!pageable) return;
          wheel.current += event.deltaY;
          if (Math.abs(wheel.current) < 120) return;
          page(wheel.current > 0 ? 1 : -1, 60);
          wheel.current = 0;
        }}
      >
        <div className={own.axis}>
          {hours.map((minute) => (
            <span key={minute} className={own.axisLabel} style={{ top: `${String(y(minute))}px` }}>
              {clockOfMinutes(minute)}
            </span>
          ))}
          {fullRuns.map(([from, to]) => (
            <i
              key={from}
              className={own.full}
              style={{
                top: `${String(y(from))}px`,
                height: `${String((to - from) * pxPerMinute)}px`,
              }}
              title={`${clockOfMinutes(from)}–${clockOfMinutes(to)}: every shown chair is busy`}
              data-testid="all-chairs-busy"
            />
          ))}
          {nowAt !== null && nowAt > opens && nowAt < closes && (
            <span className={styles.nowTag} style={{ top: `${String(y(nowAt))}px` }}>
              {clockOfMinutes(Math.floor(nowAt))}
            </span>
          )}
        </div>

        {columns.length === 0 && <div className={own.empty}>Nothing matches these filters.</div>}

        {columns.map((column) => {
          const items = placed.filter((item) => column.holds(item.appointment));
          // Side by side wherever two cards would overlap as drawn (CZ-D3).
          const lanes = packLanes(
            items.map((item) => ({
              id: item.appointment.id,
              startsAt: item.startsAt,
              endsAt: Math.max(item.endsAt, item.startsAt + CARD_MIN_PX / pxPerMinute),
            })),
          );
          const location =
            column.chair === undefined ? null : locationOf.get(column.chair.branchId);
          const day = location?.days[0];
          const canBookHere =
            onSlot !== undefined &&
            column.chair !== undefined &&
            location?.branch.permissions.includes("appointments.book") === true;

          return (
            <div
              key={column.key}
              className={
                hourWindow === null ? own.column : `${own.column ?? ""} ${styles.windowed ?? ""}`
              }
              data-testid="combined-column"
              data-column-key={column.key}
              data-column-branch={column.chair?.branchId}
            >
              {hours.map((minute) => (
                <div key={minute}>
                  <div className={styles.hourLine} style={{ top: `${String(y(minute))}px` }} />
                  <div className={styles.halfLine} style={{ top: `${String(y(minute - 30))}px` }} />
                </div>
              ))}

              {/* Closed time, per chair: before opening and after closing, or all day. */}
              {location !== null && location !== undefined && day !== undefined && (
                <ClosedShade
                  isOpen={day.isOpen}
                  opensAt={day.opensAt}
                  closesAt={day.closesAt}
                  from={opens}
                  to={closes}
                  y={y}
                  name={location.branch.name}
                />
              )}

              {canBookHere &&
                column.chair !== undefined &&
                slotsFor(day?.opensAt ?? null, day?.closesAt ?? null, opens, closes).map(
                  (minute) => (
                    <button
                      key={minute}
                      type="button"
                      className={own.slot}
                      style={{
                        top: `${String(y(minute))}px`,
                        height: `${String(30 * pxPerMinute)}px`,
                      }}
                      aria-label={`Book ${location?.branch.name ?? ""} ${column.chair?.name ?? ""} at ${clockOfMinutes(minute)}`}
                      onClick={() => {
                        if (column.chair !== undefined)
                          onSlot(column.chair.branchId, column.chair.id, minute);
                      }}
                      data-testid="combined-slot"
                      // An empty slot to the quick ring (STORY-221): where and when it is.
                      data-ring="cal.slot"
                      data-branch-id={column.chair?.branchId}
                      data-chair-id={column.chair?.id}
                      data-minute={minute}
                    />
                  ),
                )}

              {items.map((item) => {
                const [lane, of] = lanes.get(item.appointment.id) ?? [0, 1];
                const top = y(item.startsAt);
                const height = Math.max(y(item.endsAt) - top - 4, CARD_MIN_PX);
                const compact = compactColumns || of > 1;
                const state = stateOf(item.appointment, awaitingShown);
                const clash = clashOf(item.appointment, item.location.branch.id);
                const chairName =
                  item.location.chairs.find((chair) => chair.id === item.appointment.chairId)
                    ?.name ?? null;
                const detail =
                  by === "doctors"
                    ? chairName
                    : item.appointment.providerId === null
                      ? null
                      : providerName(item.appointment.providerId);
                const badge = (
                  <LocationBadge
                    code={item.location.branch.code}
                    hue={item.location.branch.hue}
                    name={item.location.branch.name}
                    size={compact ? "small" : "normal"}
                  />
                );
                const dragging = drag?.id === item.appointment.id ? drag : null;
                const draggable =
                  onMove !== undefined &&
                  item.appointment.chairId !== null &&
                  item.location.branch.permissions.includes("appointments.move") &&
                  state !== "completed" &&
                  state !== "no-show";
                const className = [
                  styles.block,
                  stateClass(state),
                  compact ? styles.compact : undefined,
                  clash !== null ? styles.twice : undefined,
                  item.appointment.id === selectedId ? styles.selected : undefined,
                ]
                  .filter((name): name is string => name !== undefined)
                  .join(" ");

                return (
                  <button
                    key={item.appointment.id}
                    type="button"
                    className={className}
                    data-card=""
                    data-testid="appointment-row"
                    data-id={item.appointment.id}
                    data-branch-id={item.location.branch.id}
                    // A booking is a thing to the quick ring (STORY-221).
                    data-ring="cal.visit"
                    data-state={state}
                    style={{
                      top: `${String(top)}px`,
                      height: `${String(height)}px`,
                      left: `calc(${String((100 / of) * lane)}% + 3px)`,
                      right: "auto",
                      width: `calc(${String(100 / of)}% - 6px)`,
                      ...cardColour(item.appointment),
                      ...(dragging === null
                        ? {}
                        : { transform: `translateY(${String(dragging.dy)}px)`, zIndex: 5 }),
                      ...(draggable ? { touchAction: "none" } : {}),
                    }}
                    onPointerDown={(event) => {
                      if (!draggable || event.button !== 0) return;
                      // Capture is a help, never a requirement: a pointer the browser
                      // no longer knows throws, and that must not stop the drag.
                      try {
                        event.currentTarget.setPointerCapture(event.pointerId);
                      } catch {
                        /* moves still arrive on the card */
                      }
                      setDrag({ id: item.appointment.id, fromY: event.clientY, dy: 0 });
                    }}
                    onPointerMove={(event) => {
                      if (dragging === null) return;
                      setDrag({ ...dragging, dy: event.clientY - dragging.fromY });
                    }}
                    onPointerUp={(event) => {
                      if (dragging === null) return;
                      setDrag(null);
                      if (Math.abs(dragging.dy) < DRAG_SLOP) return; // a click: onClick selects
                      event.preventDefault();
                      try {
                        event.currentTarget.releasePointerCapture(event.pointerId);
                      } catch {
                        /* never captured */
                      }
                      const under = document
                        .elementFromPoint(event.clientX, event.clientY)
                        ?.closest<HTMLElement>('[data-testid="combined-column"]');
                      const target = under?.dataset.columnKey;
                      const targetBranch = under?.dataset.columnBranch;
                      if (target !== column.key) {
                        // Another location's chairs: say how; anywhere else, just go back.
                        if (targetBranch !== undefined && targetBranch !== item.location.branch.id)
                          onRefused?.(OTHER_LOCATION_DROP);
                        return;
                      }
                      const moved = Math.round(dragging.dy / pxPerMinute / DRAG_STEP) * DRAG_STEP;
                      if (moved === 0 || item.appointment.chairId === null) return;
                      onMove?.(
                        item.appointment,
                        instantOfWallClock(
                          date,
                          clockOfMinutes(item.startsAt + moved),
                          item.location.branch.timezone,
                        ),
                        item.appointment.chairId,
                      );
                    }}
                    onPointerCancel={() => setDrag(null)}
                    title={`${item.location.branch.name}${chairName === null ? "" : ` ${chairName}`}${
                      clash === null ? "" : ` · also booked at ${clash}`
                    }`}
                    onClick={(event) => {
                      // The click that ends a drag does not also select.
                      if (event.defaultPrevented) return;
                      onSelect(item.appointment);
                    }}
                  >
                    {state === "awaiting" && (
                      <span className={styles.stateMark} aria-hidden="true" />
                    )}
                    <CardBody
                      appointment={item.appointment}
                      startMinute={item.startsAt}
                      endMinute={item.endsAt}
                      detail={detail}
                      // A narrow chair column's head already names its location,
                      // so its compact cards leave the badge out and keep the time.
                      badge={
                        compact ? (
                          by === "chairs" ? undefined : (
                            badge
                          )
                        ) : (
                          <span className={styles.badgeCorner}>{badge}</span>
                        )
                      }
                      compact={compact}
                      roomForDetail
                      stateLabel={
                        clash === null ? STATE_WORDS[state] : `doctor also booked at ${clash}`
                      }
                    />
                  </button>
                );
              })}

              {nowAt !== null && nowAt > opens && nowAt < closes && (
                <div className={styles.now} style={{ top: `${String(y(nowAt))}px` }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  function buildColumns(): Column[] {
    if (by === "chairs") {
      return shown.flatMap((location) =>
        location.chairs
          .filter((chair) => !filters.chairIds.includes(chair.id))
          .map((chair) => ({
            key: chair.id,
            chair: { ...chair, branchId: location.branch.id },
            holds: (appointment: Appointment) => appointment.chairId === chair.id,
            head: (
              <button
                type="button"
                className={styles.colButton}
                aria-pressed={onlyColumn === chair.id}
                title={
                  onlyColumn === chair.id
                    ? "Show every chair"
                    : `Show only ${location.branch.name} ${chair.name}`
                }
                onClick={() => onColumnName("chairIds", chair.id)}
                data-testid="column-only"
              >
                <LocationBadge
                  code={location.branch.code}
                  hue={location.branch.hue}
                  name={location.branch.name}
                />
                <span className={own.headName}>{chair.name}</span>
                <span className={own.headShort}>{chairNumber(chair.name)}</span>
              </button>
            ),
          })),
      );
    }

    // Doctors: everyone working at a shown location that day, and anyone with a
    // booking in view; then a column for bookings nobody is named on.
    const ids: string[] = [];
    for (const location of shown) {
      for (const provider of location.providers)
        if (!ids.includes(provider.userId)) ids.push(provider.userId);
    }
    for (const item of placed) {
      const id = item.appointment.providerId;
      if (id !== null && !ids.includes(id)) ids.push(id);
    }
    const doctors = ids
      .filter((id) => !filters.providerIds.includes(id))
      .filter((id) => filters.onlyProviderId === null || id === filters.onlyProviderId)
      .map((id) => ({ id, name: providerName(id) }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const out: Column[] = doctors.map((doctor) => ({
      key: doctor.id,
      providerId: doctor.id,
      holds: (appointment: Appointment) => appointment.providerId === doctor.id,
      head: (
        <button
          type="button"
          className={styles.colButton}
          aria-pressed={onlyColumn === doctor.id}
          title={onlyColumn === doctor.id ? "Show every doctor" : `Show only ${doctor.name}`}
          onClick={() => onColumnName("providerIds", doctor.id)}
          data-testid="column-only"
        >
          <span className={own.avatar} aria-hidden="true">
            {initialsOf(doctor.name)}
          </span>
          <span className={own.headName}>{doctor.name}</span>
        </button>
      ),
    }));

    // Unnamed bookings get their own column — unless the doctors are being filtered.
    if (
      filters.onlyProviderId === null &&
      filters.providerIds.length === 0 &&
      placed.some((item) => item.appointment.providerId === null)
    ) {
      out.push({
        key: "unassigned",
        providerId: null,
        holds: (appointment: Appointment) => appointment.providerId === null,
        head: (
          <span title="Bookings with no doctor yet — open one to assign a doctor">
            <span className={own.headName}>Doctor to assign</span>
            <span className={own.headShort}>—</span>
          </span>
        ),
      });
    }
    return out;
  }

  /** The other location, when this booking's doctor is booked there at the same time. */
  function clashOf(appointment: Appointment, branchId: string): string | null {
    if (appointment.providerId === null || appointment.patient === null) return null;
    const mine = placed.find((item) => item.appointment.id === appointment.id);
    if (mine === undefined) return null;
    const other = placed.find(
      (item) =>
        item.appointment.providerId === appointment.providerId &&
        item.location.branch.id !== branchId &&
        item.appointment.status !== "NO_SHOW" &&
        item.startsAt < mine.endsAt &&
        mine.startsAt < item.endsAt,
    );
    return other === undefined ? null : other.location.branch.name;
  }
}

/** The clock's gutter, in px — the axis labels live in it. */
const GUTTER = 52;

function template(columns: number): string {
  return `${String(GUTTER)}px repeat(${String(Math.max(columns, 1))}, minmax(0, 1fr))`;
}

function ClosedShade({
  isOpen,
  opensAt,
  closesAt,
  from,
  to,
  y,
  name,
}: {
  isOpen: boolean;
  opensAt: string | null;
  closesAt: string | null;
  from: number;
  to: number;
  y: (minute: number) => number;
  name: string;
}): React.JSX.Element | null {
  if (!isOpen || opensAt === null || closesAt === null) {
    return <div className={own.closed} style={{ top: 0, bottom: 0 }} title={`${name} is closed`} />;
  }
  const open = minutesOfClock(opensAt);
  const close = minutesOfClock(closesAt);
  return (
    <>
      {open > from && (
        <div
          className={own.closed}
          style={{ top: 0, height: `${String(y(open))}px` }}
          title={`${name} opens at ${opensAt}`}
        />
      )}
      {close < to && (
        <div
          className={own.closed}
          style={{ top: `${String(y(close))}px`, bottom: 0 }}
          title={`${name} closes at ${closesAt}`}
        />
      )}
    </>
  );
}

/** Half-hour starts a slot button covers: the location's open hours, or nothing when shut. */
function slotsFor(
  opensAt: string | null,
  closesAt: string | null,
  from: number,
  to: number,
): number[] {
  if (opensAt === null || closesAt === null) return [];
  const out: number[] = [];
  for (
    let t = Math.max(from, minutesOfClock(opensAt));
    t < Math.min(to, minutesOfClock(closesAt));
    t += 30
  )
    out.push(t);
  return out;
}

function nowMinutes(timezone: string | null, date: string, now: Date = new Date()): number | null {
  if (timezone === null) return null;
  const here = wallClock(now.toISOString(), timezone);
  return here.date === date ? here.minutes : null;
}

const STATE_WORDS: Partial<Record<ReturnType<typeof stateOf>, string>> = {
  "in-chair": "In the chair",
  completed: "Completed",
  awaiting: "Awaiting validation",
  "no-show": "No-show",
};

function stateClass(state: ReturnType<typeof stateOf>): string | undefined {
  switch (state) {
    case "completed":
      return styles.completed;
    case "in-chair":
      return styles.inChair;
    case "awaiting":
      return styles.awaiting;
    case "held":
      return styles.held;
    case "no-show":
      return styles.noShow;
    default:
      return undefined;
  }
}

/** `Dana Haddad` → `DH`; the round avatar in a doctor's column head. */
export function initialsOf(name: string): string {
  return name
    .replace(/^dr\.?\s+/i, "")
    .split(/\s+/)
    .filter((word) => word !== "")
    .map((word) => word[0] ?? "")
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/** In a narrow head a chair is its number, when its name has one; otherwise its name. */
function chairNumber(name: string): string {
  const match = /(\d+)\s*$/.exec(name);
  return match?.[1] ?? name;
}
